import React from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import { connect } from 'react-redux';

import AttendeeTicketNotification from '@eb/attendee-ticket-notification';
import { trackAnalyticsEvent } from '@eb/site-analytics';
import { isMediumNarrowDown } from '@eb/eds-utils';
import { Layout } from '@eb/eds-layout';

import { BasePage } from '../../../base';
import { getRestOfContext } from '../../redux/root';
import { runSearch } from '../../redux/search';
import { updateHeaderImage } from '../../redux/header';
import { ConsumerHeader } from '../../../../components/ConsumerHeader';
import withStickBehavior from '../../hoc/withStickyBehavior';
import {
    BUCKETS_PROPTYPES,
    FEATURE_FLAGS_PROPTYPES,
    ORGANIZER_ARTICLE_BUCKET_PROPTYPE,
    FLAT_BUCKET_PROPTYPE,
} from '../../../../constants/propTypes';
import { GA_HOME_FEED_CATEGORY } from '../../../../constants/analytics';
import { PLACE_OBJECT_PROPTYPE } from '../../../../constants';
import { getFeedTrackingProperties } from '../../utils';

const HeaderContainer = loadable(() => import('../HeaderContainer'), {
    resolveComponent: (components) => components.HeaderContainer,
});
const FullbleedHeaderContainer = loadable(
    () => import('../FullbleedHeaderContainer'),
    { resolveComponent: (components) => components.FullbleedHeaderContainer },
);
const BrowseContainer = loadable(() => import('../BrowseContainer'), {
    resolveComponent: (components) => components.BrowseContainer,
});

const USBannerContainer = loadable(() => import('../USBannerContainer'), {
    resolveComponent: (components) => components.USBannerContainer,
});

const EnhancedHomeConsumerHeader = withStickBehavior(ConsumerHeader);

//Connect BasePage with extra content
const _mapStateToProps = ({ app, user, content }, ownProps) => ({
    ...ownProps,
    isMobile: app.isMobile,
    isAuthenticated: user.isAuthenticated,
    userId: user.publicId,
    featureFlags: app.featureFlags,
    seasonalContent: content.seasonal,
});

const _mapDispatchToProps = {
    handleSearch: runSearch,
};

const ConnectedBasePage = connect(
    _mapStateToProps,
    _mapDispatchToProps,
)(BasePage);

const applyIEFixes = () => {
    /**
     * IE is returning errors because SVG elements are not correctly mapped when parsing the docs.
     * This post gives a solution to this: https://github.com/Pomax/react-onclickoutside#ie-does-not-support-classlist-for-svg-elements
     */
    if (!('classList' in SVGElement.prototype)) {
        Object.defineProperty(SVGElement.prototype, 'classList', {
            get() {
                return {
                    contains: (className) =>
                        this.className.baseVal.split(' ').indexOf(className) !==
                        -1,
                };
            },
        });
    }
};

// Deal with the messed up Varnish urls (/esi_cahe/?...) which break the signin link
const fixLoginUrl = (loginUrl) => {
    const splitUrl = loginUrl.split('/');
    const urlBeginning = splitUrl.slice(0, -1);

    return urlBeginning.concat('?referrer=%2F').join('/');
};

export class HomeBaseContainer extends React.Component {
    static propTypes = {
        // feed props
        buckets: BUCKETS_PROPTYPES,
        featureFlags: FEATURE_FLAGS_PROPTYPES,
        placeId: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        currentPlace: PropTypes.string,
        currentPlaceParent: PropTypes.string,
        locations: PropTypes.shape({
            places: PropTypes.arrayOf(PLACE_OBJECT_PROPTYPE),
        }),
        placeType: PropTypes.string,
        slug: PropTypes.string,
        flatBucket: FLAT_BUCKET_PROPTYPE,
        isAuthenticated: PropTypes.bool,
        userId: PropTypes.string,
        handleRestOfContext: PropTypes.func,
        originalContext: PropTypes.object,
        history: PropTypes.object,
        env: PropTypes.object,
    };

    static defaultProps = {
        handleRestOfContext: () => null,
    };

    constructor(props) {
        super(props);

        this.state = {
            isInBrowser: false,
            // Whether user is enrolled in the "search bar on homepage" experiment with Optimize
            // we don't want to default to variant A because we want to show something different than
            // A while we wait for Optimize to return
            isInSearchBarExperimentVariant: null,
            isMobile: false,
        };

        props.updateHeaderImage();
    }

    componentDidMount() {
        const {
            handleRestOfContext,
            originalContext,
            isAuthenticated,
            userId,
        } = this.props;

        applyIEFixes();
        handleRestOfContext(originalContext);

        this.setState({
            isInBrowser: true,
            isMobile: isMediumNarrowDown(),
        });

        const feedTrackingProperties = getFeedTrackingProperties(
            isAuthenticated,
            userId,
            {},
        );

        trackAnalyticsEvent(feedTrackingProperties);
    }

    render() {
        const { header } = this.props;

        // During the server render, because of Varnish, we want to render the page
        // as a logged-out user. Once we are in the browser, we have the props for the user
        // and we want to update the page. Since most of our components do not keep this in mind,
        // we basically have to do it with componentDidMount or else we get weird markup mismatch issues.
        // Once we're in the browser, we'll go to use the normal props.
        // See also the ConsumerHeader/index.js
        const overrideProps = this.state.isInBrowser
            ? {}
            : {
                  env: {
                      ...this.props.env,
                      loginUrl: fixLoginUrl(this.props.env.loginUrl),
                      signinUrl: fixLoginUrl(this.props.env.signinUrl),
                      signupUrl: fixLoginUrl(this.props.env.signupUrl),
                      showCookieHeader: false,
                  },
                  footer: <div />,
                  user: {
                      isAuthenticated: false,
                      publicId: null,
                      canCreateEvents: false,
                  },
              };

        const useNewHeader = header?.forceSpecialStyles;

        return (
            <ConnectedBasePage
                {...this.props}
                {...overrideProps}
                isHomePage={true}
                structureHeader={EnhancedHomeConsumerHeader}
            >
                {useNewHeader && (
                    <FullbleedHeaderContainer history={this.props.history} />
                )}
                <Layout hasHorizontalGutters={true} maxWidth="large">
                    <AttendeeTicketNotification
                        shouldAnimateEntry={true}
                        isAuthenticated={this.props.isAuthenticated}
                        containerStyles={
                            this.state.isMobile
                                ? { margin: '0 auto', top: '120px' }
                                : { right: '32px', top: '120px' }
                        }
                        onLinkClick={(eid, hasDigitalContent) =>
                            trackAnalyticsEvent({
                                category: GA_HOME_FEED_CATEGORY,
                                label: eid,
                                action: hasDigitalContent
                                    ? 'UpcomingOnlineEventViewed'
                                    : 'UpcomingTicketViewed',
                            })
                        }
                        onDismiss={(timeUntilEvent, hasDigitalContent) =>
                            trackAnalyticsEvent({
                                category: GA_HOME_FEED_CATEGORY,
                                label: timeUntilEvent,
                                action: hasDigitalContent
                                    ? 'UpcomingOnlineEventDismissed'
                                    : 'UpcomingTicketDismissed',
                            })
                        }
                    />
                    <>
                        <div className="eds-l-pad-top-2 eds-l-mar-top-5">
                            <USBannerContainer />
                        </div>

                        {!useNewHeader && (
                            <HeaderContainer history={this.props.history} />
                        )}
                    </>

                    <BrowseContainer />
                </Layout>
            </ConnectedBasePage>
        );
    }
}

const _mapHomeStateToProps = ({ app, user, env, header }) => ({
    isMobile: app.isMobile,
    isAuthenticated: user.isAuthenticated,
    userId: user.publicId,
    env,
    app,
    header,
});

const _mapHomeDispatchToProps = {
    handleSearch: runSearch,
    handleRestOfContext: getRestOfContext,
    updateHeaderImage: updateHeaderImage,
};

export default connect(
    _mapHomeStateToProps,
    _mapHomeDispatchToProps,
)(HomeBaseContainer);
